import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Package, Clock, CheckCircle, X, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useImpersonation } from "@/hooks/useImpersonation";
import { useLanguage } from "@/hooks/useLanguage";
import { formatPriceWithCurrency } from "@/utils/currency";
import { useCurrency } from "@/hooks/useCurrency";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { OrderFilters as BuyerOrderFilters } from "@/components/buyer/OrderFilters";
import { useProductTranslation } from "@/hooks/useProductTranslation";
import { usePersistentTabState } from "@/hooks/usePersistentTabState";

const ITEMS_PER_PAGE = 20;

interface Order {
  id: string;
  order_number: string;
  status: string;
  amount_cents: number;
  currency: string;
  created_at: string;
  paid_at: string | null;
  available_at: string | null;
  picked_up_at: string | null;
  expires_at: string | null;
  product_id: string;
  products?: {
    title: string;
    images: string[] | null;
  };
}

const OrderStatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'PENDING_PAYMENT':
      return <Clock className="h-4 w-4 text-orange-500" />;
    case 'PAID':
    case 'PREPARING':
      return <Package className="h-4 w-4 text-blue-500" />;
    case 'READY_FOR_PICKUP':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'PICKED_UP':
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    case 'CANCELLED':
    case 'EXPIRED':
      return <X className="h-4 w-4 text-red-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-500" />;
  }
};

const statusFallbacks: Record<string, Record<string, string>> = {
  fr: {
    PENDING_PAYMENT: 'En attente de paiement',
    PAID: 'Payé',
    PREPARING: 'En préparation',
    READY_FOR_PICKUP: 'Prêt pour retrait',
    AVAILABLE_FOR_PICKUP: 'Disponible pour retrait',
    PICKED_UP: 'Récupéré',
    CANCELLED: 'Annulé',
    EXPIRED: 'Expiré',
  },
  en: {
    PENDING_PAYMENT: 'Pending payment',
    PAID: 'Paid',
    PREPARING: 'Preparing',
    READY_FOR_PICKUP: 'Ready for pickup',
    AVAILABLE_FOR_PICKUP: 'Available for pickup',
    PICKED_UP: 'Picked up',
    CANCELLED: 'Cancelled',
    EXPIRED: 'Expired',
  },
  'lg-fr': {
    PENDING_PAYMENT: 'Ezali kozela kofuta',
    PAID: 'Efutami',
    PREPARING: 'Ezali kobongisama',
    READY_FOR_PICKUP: 'Ezali pene ya kozwa',
    AVAILABLE_FOR_PICKUP: 'Ezali pene ya kozwa',
    PICKED_UP: 'Ezuami',
    CANCELLED: 'Ebatalaki',
    EXPIRED: 'Etikali',
  },
  sw: {
    PENDING_PAYMENT: 'Inasubiri malipo',
    PAID: 'Imelipwa',
    PREPARING: 'Inaandaliwa',
    READY_FOR_PICKUP: 'Tayari kuchukua',
    AVAILABLE_FOR_PICKUP: 'Iko tayari kuchukua',
    PICKED_UP: 'Imechukuliwa',
    CANCELLED: 'Imeghairiwa',
    EXPIRED: 'Imeisha muda',
  },
};

const getStatusLabel = (status: string, _t: (key: string, fallback?: string) => string, language: string) => {
  // Use reliable local fallbacks to avoid DB inconsistencies
  const fallback = statusFallbacks[language]?.[status] ?? statusFallbacks['fr'][status] ?? status;
  return fallback;
};

const getStatusVariant = (status: string) => {
  switch (status) {
    case 'PENDING_PAYMENT':
      return 'secondary';
    case 'PAID':
    case 'PREPARING':
      return 'default';
    case 'READY_FOR_PICKUP':
      return 'default';
    case 'PICKED_UP':
      return 'default';
    case 'CANCELLED':
    case 'EXPIRED':
      return 'destructive';
    default:
      return 'secondary';
  }
};

const Orders = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getEffectiveUserId } = useImpersonation();
  const { t } = useLanguage();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);

  // Filtres
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [activeTab, setActiveTab] = usePersistentTabState(
    'orders-page:activeTab',
    'all',
    ['all', 'active', 'completed', 'cancelled']
  );

  const handleRefresh = async () => {
    setCurrentPage(0);
    setHasMore(true);
    await fetchOrders(true);
  };

  const { containerRef, shouldShowIndicator, isRefreshing } = usePullToRefresh({
    onRefresh: handleRefresh,
    threshold: 80
  });

  const { observerTarget } = useInfiniteScroll({
    onLoadMore: () => fetchOrders(false, currentPage + 1),
    hasMore,
    isLoading: loadingMore
  });

  useEffect(() => {
    const effectiveUserId = getEffectiveUserId();
    if (effectiveUserId) {
      handleRefresh();
    }

    // Subscribe to real-time order updates
    const channel = supabase
      .channel('orders-list-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `client_id=eq.${effectiveUserId}`
        },
        () => {
          if (effectiveUserId) {
            handleRefresh();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [getEffectiveUserId]);

  const fetchOrders = async (isRefresh = false, page = 0) => {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) return;

    try {
      if (isRefresh) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const from = page * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          products (
            title,
            images
          )
        `)
        .eq('client_id', effectiveUserId)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;
      
      if (isRefresh || page === 0) {
        setOrders(data || []);
      } else {
        setOrders(prev => [...prev, ...(data || [])]);
      }

      setCurrentPage(page);
      setHasMore((data || []).length === ITEMS_PER_PAGE);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const filterOrdersByStatus = (status: string) => {
    switch (status) {
      case 'active':
        return filteredAndSortedOrders.filter(order => 
          ['PENDING_PAYMENT', 'PAID', 'PREPARING', 'READY_FOR_PICKUP'].includes(order.status)
        );
      case 'completed':
        return filteredAndSortedOrders.filter(order => order.status === 'PICKED_UP');
      case 'cancelled':
        return filteredAndSortedOrders.filter(order => 
          ['CANCELLED', 'EXPIRED'].includes(order.status)
        );
      default:
        return filteredAndSortedOrders;
    }
  };

  // Filtrer et trier les commandes
  const filteredAndSortedOrders = orders
    .filter(order => {
      // Filtre par statut
      if (statusFilter !== 'all' && order.status !== statusFilter) return false;

      // Filtre par recherche
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          order.order_number.toLowerCase().includes(query) ||
          order.products?.title.toLowerCase().includes(query)
        );
      }

      // Filtre par date
      if (dateFilter !== 'all') {
        const orderDate = new Date(order.created_at);
        const now = new Date();
        
        switch (dateFilter) {
          case 'today':
            if (orderDate.toDateString() !== now.toDateString()) return false;
            break;
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            if (orderDate < weekAgo) return false;
            break;
          case 'month':
            if (orderDate.getMonth() !== now.getMonth() || orderDate.getFullYear() !== now.getFullYear()) return false;
            break;
          case 'year':
            if (orderDate.getFullYear() !== now.getFullYear()) return false;
            break;
        }
      }

      return true;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'amount':
          comparison = a.amount_cents - b.amount_cents;
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'order_number':
          comparison = a.order_number.localeCompare(b.order_number);
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  const clearFilters = () => {
    setStatusFilter('all');
    setSearchQuery('');
    setDateFilter('all');
    setSortBy('created_at');
    setSortOrder('desc');
  };

  const handleToggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <div className="space-y-3">
                      <div className="h-4 bg-muted rounded w-1/3"></div>
                      <div className="h-3 bg-muted rounded w-1/2"></div>
                      <div className="h-3 bg-muted rounded w-1/4"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container py-4 md:py-8" ref={containerRef}>
        {/* Pull to Refresh Indicator */}
        {shouldShowIndicator && (
          <div className="fixed top-16 left-0 right-0 z-50 flex justify-center">
            <div className="bg-primary text-primary-foreground px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="text-sm">
                {isRefreshing ? t('orders.refreshing', 'Actualisation...') : t('orders.release_refresh', 'Relâchez pour actualiser')}
              </span>
            </div>
          </div>
        )}
        {/* Breadcrumb */}
        <div className="mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard') } className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('orders.back_dashboard', 'Retour au compte')}
          </Button>
        </div>

        {/* Header */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl md:text-3xl">{t('orders.title', 'Mes commandes')}</CardTitle>
                <p className="text-muted-foreground mt-1">
                  {t('orders.subtitle', 'Suivez l\'état de toutes vos commandes')}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                {t('common.refresh', 'Actualiser')}
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Filtres */}
        <BuyerOrderFilters
          statusFilter={statusFilter}
          searchQuery={searchQuery}
          dateFilter={dateFilter}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onStatusChange={setStatusFilter}
          onSearchChange={setSearchQuery}
          onDateChange={setDateFilter}
          onSortByChange={setSortBy}
          onSortOrderToggle={handleToggleSortOrder}
          onClearFilters={clearFilters}
        />

        {/* Orders Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 mb-6">
                <TabsTrigger value="all" className="gap-2">
                  <Package className="h-4 w-4" />
                  {t('orders.tabs.all', 'Toutes')}
                  <Badge variant="secondary" className="ml-1">{filteredAndSortedOrders.length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="active" className="gap-2">
                  <Clock className="h-4 w-4" />
                  {t('orders.tabs.active', 'En cours')}
                  <Badge variant="secondary" className="ml-1">{filterOrdersByStatus('active').length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="completed" className="gap-2">
                  <CheckCircle className="h-4 w-4" />
                  {t('orders.tabs.completed', 'Terminées')}
                  <Badge variant="secondary" className="ml-1">{filterOrdersByStatus('completed').length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="cancelled" className="gap-2">
                  <X className="h-4 w-4" />
                  {t('orders.tabs.cancelled', 'Annulées')}
                  <Badge variant="secondary" className="ml-1">{filterOrdersByStatus('cancelled').length}</Badge>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="all">
                <OrdersList orders={filteredAndSortedOrders} t={t} />
              </TabsContent>

              <TabsContent value="active">
                <OrdersList orders={filterOrdersByStatus('active')} t={t} />
              </TabsContent>

              <TabsContent value="completed">
                <OrdersList orders={filterOrdersByStatus('completed')} t={t} />
              </TabsContent>

              <TabsContent value="cancelled">
                <OrdersList orders={filterOrdersByStatus('cancelled')} t={t} />
              </TabsContent>
            </CardContent>
          </Card>
        </Tabs>

        {/* Infinite Scroll Observer */}
        {hasMore && !loading && orders.length > 0 && (
          <div ref={observerTarget} className="py-8 flex justify-center">
            {loadingMore && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>{t('orders.loading', 'Chargement...')}</span>
              </div>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

interface OrdersListProps {
  orders: Order[];
  t: (key: string, fallback?: string) => string;
}

const OrderCard = ({ order, t, language }: { order: Order; t: any; language: string }) => {
  const { displayCurrency, convertPrice } = useCurrency();
  const navigate = useNavigate();
  const { title } = useProductTranslation(
    order.product_id,
    order.products?.title || 'Produit',
    ''
  );

  return (
    <Card className="hover:shadow-lg transition-all cursor-pointer border-l-4" 
      style={{ 
        borderLeftColor: 
          ['PENDING_PAYMENT'].includes(order.status) ? '#f97316' :
          ['PAID', 'PREPARING'].includes(order.status) ? '#3b82f6' :
          ['READY_FOR_PICKUP'].includes(order.status) ? '#10b981' :
          ['PICKED_UP'].includes(order.status) ? '#22c55e' :
          '#ef4444'
      }}
      onClick={() => navigate(`/orders/${order.id}`)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Product Image */}
          {order.products?.images?.[0] && (
            <div className="w-20 h-20 bg-muted rounded-lg overflow-hidden flex-shrink-0">
              <img
                src={order.products.images[0]}
                alt={title}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          {/* Order Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm md:text-base line-clamp-2 mb-1">{title}</h3>
                <p className="text-xs text-muted-foreground font-mono truncate">
                  {t('orders.order', 'Commande')} #{order.order_number}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <OrderStatusIcon status={order.status} />
                <Badge variant={getStatusVariant(order.status) as any} className="whitespace-nowrap text-[10px] md:text-xs px-1.5 py-0.5 md:px-2">
                  {getStatusLabel(order.status, t, language)}
                </Badge>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mt-3">
              <div className="flex flex-col gap-1 min-w-0 flex-1">
                <p className="text-xs text-muted-foreground truncate">
                  {t('orders.placed_on', 'Passée le')} {new Date(order.created_at).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}
                </p>
                <p className="text-base md:text-lg font-bold">
                  {formatPriceWithCurrency(order.amount_cents, order.currency, displayCurrency, convertPrice)}
                </p>
              </div>

              <div className="flex gap-2 w-full md:w-auto">
                {order.status === 'PENDING_PAYMENT' && (
                  <Button size="sm" className="flex-1 md:flex-none" onClick={(e) => { e.stopPropagation(); navigate(`/orders/${order.id}`); }}>
                    {t('orders.pay_now', 'Payer')}
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex-1 md:flex-none"
                  onClick={(e) => { e.stopPropagation(); navigate(`/orders/${order.id}`); }}
                >
                  {t('orders.details', 'Détails')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const OrdersList = ({ orders, t }: OrdersListProps) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  if (orders.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold mb-2">{t('orders.no_orders', 'Aucune commande')}</h3>
          <p className="text-muted-foreground mb-6">
            {t('orders.no_orders_message', 'Vous n\'avez pas encore passé de commande.')}
          </p>
          <Button onClick={() => navigate('/products')}>
            {t('orders.start_shopping', 'Commencer mes achats')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {orders.map((order) => (
        <OrderCard key={order.id} order={order} t={t} language={language} />
      ))}
    </div>
  );
};

export default Orders;